import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import {
  LeagueService,
  SeasonService,
  LeagueSeasonService,
  ContestantService,
  RealtimeService,
} from '../../services/firebaseService';
import { DraftService } from '../../services/draftService';
import DraftRoomBoard from '../../components/draft/DraftRoomBoard';
import {
  DRAFT_STATUS,
  findRoomForTeam,
  getCurrentTeamId,
  getTeamPickPosition,
} from '../../utils/draftUtils';
import { getUserClaimedTeams, getTeamName } from '../../utils/teamUtils';

export default function PlayerDraft() {
  const { leagueId, seasonId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [league, setLeague] = useState(null);
  const [globalSeason, setGlobalSeason] = useState(null);
  const [leagueSeason, setLeagueSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [contestants, setContestants] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);

  const myTeamIds = useMemo(() => {
    const claimed = getUserClaimedTeams(userProfile?.teams || {});
    return claimed
      .filter(c => c.leagueId === leagueId && c.seasonId === seasonId)
      .map(c => c.teamId);
  }, [userProfile, leagueId, seasonId]);

  const draftRooms = leagueSeason?.draftRooms || [];
  const draftStatus = leagueSeason?.draftStatus || DRAFT_STATUS.NOT_STARTED;
  const draftComplete =
    draftStatus === DRAFT_STATUS.COMPLETE ||
    draftStatus === DRAFT_STATUS.FINALIZED;
  const isFinalized = draftStatus === DRAFT_STATUS.FINALIZED;

  const myRooms = useMemo(
    () =>
      draftRooms.filter(r =>
        (r.teamOrder || []).some(id => myTeamIds.includes(id))
      ),
    [draftRooms, myTeamIds]
  );

  const viewableRooms = isFinalized ? draftRooms : myRooms;
  const canSwitchRooms = isFinalized
    ? draftRooms.length > 1
    : draftComplete && myRooms.length > 1;

  const activeRoom =
    viewableRooms.find(r => r.id === activeRoomId) || viewableRooms[0] || null;

  useEffect(() => {
    let unsubSeason = null;
    let unsubTeams = null;

    async function init() {
      try {
        setLoading(true);
        const [leagueData, seasonData, lsData] = await Promise.all([
          LeagueService.getLeague(leagueId),
          SeasonService.getSeason(seasonId),
          LeagueSeasonService.getLeagueSeason(leagueId, seasonId),
        ]);
        setLeague(leagueData);
        setGlobalSeason(seasonData);
        setLeagueSeason(lsData);

        const contestantIds = seasonData?.contestants || [];
        const all = await ContestantService.getContestantsBySeason(seasonId);
        const byId = Object.fromEntries(all.map(c => [c.id, c]));
        let list = contestantIds.map(id => byId[id] || { id, name: id });
        if (!list.length) list = all;
        setContestants(list);

        unsubSeason = RealtimeService.listenToLeagueSeason(
          leagueId,
          seasonId,
          data => {
            if (data) setLeagueSeason(data);
          }
        );
        unsubTeams = DraftService.listenToTeams(leagueId, seasonId, setTeams);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load draft');
      } finally {
        setLoading(false);
      }
    }

    init();
    return () => {
      if (unsubSeason) unsubSeason();
      if (unsubTeams) unsubTeams();
    };
  }, [leagueId, seasonId]);

  useEffect(() => {
    if (!viewableRooms.length) return;
    // Prefer a room where it's one of my teams' turn (live draft only)
    if (!isFinalized) {
      const onClock = viewableRooms.find(r => {
        const current = getCurrentTeamId(r);
        return current && myTeamIds.includes(current);
      });
      if (onClock) {
        setActiveRoomId(onClock.id);
        return;
      }
    }
    if (!activeRoomId || !viewableRooms.some(r => r.id === activeRoomId)) {
      setActiveRoomId(viewableRooms[0].id);
    }
  }, [viewableRooms, myTeamIds, activeRoomId, isFinalized]);

  const handlePick = async ({ roomId, teamId, contestantId }) => {
    try {
      setPicking(true);
      await DraftService.makePick({
        leagueId,
        seasonId,
        roomId,
        teamId,
        contestantId,
        userId: currentUser.uid,
        userProfile,
        isCommissioner: false,
      });
      toast.success('Pick submitted');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to make pick');
    } finally {
      setPicking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500" />
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-700 mb-4">
            This league does not exist or is no longer available.
          </p>
          <Link
            to="/"
            className="text-survivor-600 font-medium hover:underline"
          >
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (myTeamIds.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-700 mb-4">
            You have not claimed a team in this league season.
          </p>
          <Link
            to={`/player/claim-team/${leagueId}/${seasonId}`}
            className="text-survivor-600 font-medium hover:underline"
          >
            Claim a team
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4 min-h-[44px]"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back home
        </button>

        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Draft — {league?.name}
          </h1>
          <p className="text-gray-600 mt-1">{globalSeason?.name || seasonId}</p>
        </div>

        {draftStatus === DRAFT_STATUS.NOT_STARTED && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-gray-700">
            {draftRooms.length === 0 ? (
              <p>
                The commissioner has not set up the draft yet. Check back soon.
              </p>
            ) : (
              <div className="space-y-4">
                <p>Draft rooms are set. Waiting for the commissioner to start.</p>
                {myTeamIds.map(teamId => {
                  const room = findRoomForTeam(draftRooms, teamId);
                  const pos = room
                    ? getTeamPickPosition(room, teamId)
                    : null;
                  return (
                    <p key={teamId} className="text-sm">
                      <span className="font-medium">
                        {getTeamName(teamId, teams)}
                      </span>
                      {room && pos
                        ? ` — pick #${pos} of ${room.teamOrder.length} in Room ${room.id}`
                        : ' — not yet assigned to a room'}
                    </p>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {(draftStatus === DRAFT_STATUS.IN_PROGRESS ||
          draftStatus === DRAFT_STATUS.COMPLETE ||
          draftStatus === DRAFT_STATUS.FINALIZED) && (
          <div className="space-y-4">
            {isFinalized && (
              <div className="rounded-md bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3">
                Draft finalized. Results are locked.
              </div>
            )}
            {draftStatus === DRAFT_STATUS.COMPLETE && !isFinalized && (
              <div className="rounded-md bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3">
                Draft complete. Waiting for the commissioner to finalize.
              </div>
            )}

            {!isFinalized && myRooms.length > 1 && !draftComplete && (
              <p className="text-sm text-gray-600">
                You can switch between your draft rooms after the full draft is
                complete.
              </p>
            )}

            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <DraftRoomBoard
                room={activeRoom}
                rooms={viewableRooms}
                activeRoomId={activeRoom?.id}
                onRoomSelect={setActiveRoomId}
                canSwitchRooms={canSwitchRooms}
                teams={teams}
                contestants={contestants}
                myTeamIds={myTeamIds}
                isCommissioner={false}
                readOnly={draftStatus !== DRAFT_STATUS.IN_PROGRESS}
                onPick={handlePick}
                picking={picking}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
